/**
 * Teams — `docs/API.md`'s `/v1/teams` block. Previously specified but not
 * implemented (tracked as `teams: '§3'` in the backend's own
 * `SPECIFIED_NOT_BUILT` map); now real — `salescrmapi/src/modules/teams`.
 *
 * The caller becomes `manager_id` and the team's first member automatically
 * unless `manager_id` is given explicitly; `parent_id` defaults to the
 * caller's own `primary_team_id` — see `createTeam` below and the backend
 * service's own header note for the full "sales manager creates their team"
 * bootstrap rule this module exists for.
 */
import apiClient from './axiosClient.js';
import { withMockFallback } from './mockFallback.js';

/** @param {object} [params] any of listTeamsQuery's fields, e.g. `{ q, status, manager_id }` */
export function listTeams(params = {}) {
  return withMockFallback(
    'GET /teams',
    async () => (await apiClient.get('/teams', { params })).data,
    () => ({
      data: [],
      page: { limit: params.limit ?? 50, next_cursor: null, prev_cursor: null, has_more: false, total: 0 },
    }),
  );
}

/** One team. @param {string} id */
export function getTeam(id) {
  return withMockFallback(
    `GET /teams/${id}`,
    async () => (await apiClient.get(`/teams/${id}`)).data,
    () => null,
  );
}

/**
 * Create a team. Carries an Idempotency-Key so a double-submit cannot create
 * the same team twice.
 * @param {{code:string, name:string, business_unit_id?:string, parent_id?:string|null, manager_id?:string}} payload
 */
export function createTeam(payload) {
  return withMockFallback(
    'POST /teams',
    async () =>
      (await apiClient.post('/teams', payload, {
        headers: { 'Idempotency-Key': crypto.randomUUID() },
      })).data,
    () => ({ ...payload, id: crypto.randomUUID(), row_version: 1, member_count: 1 }),
  );
}

/**
 * Update a team under optimistic locking (§1.7).
 * @param {string} id @param {object} patch @param {number} rowVersion
 */
export function updateTeam(id, patch, rowVersion) {
  return withMockFallback(
    `PATCH /teams/${id}`,
    async () =>
      (await apiClient.patch(`/teams/${id}`, patch, {
        headers: { 'If-Match': `"${rowVersion}"` },
      })).data,
    () => ({ id, ...patch }),
  );
}

/**
 * Soft-delete a team. No seeded role below superadmin can actually call this
 * — the client doesn't need to know that; a `403` renders the same as any
 * other permission error.
 * @param {string} id @param {{reason:string, rowVersion?:number}} options
 */
export function deleteTeam(id, { reason, rowVersion } = {}) {
  return withMockFallback(
    `DELETE /teams/${id}`,
    async () =>
      (await apiClient.delete(`/teams/${id}`, {
        data: { reason, ...(rowVersion ? { row_version: rowVersion } : {}) },
      })).data,
    () => ({ id, deleted: true }),
  );
}

/** The current roster. @param {string} id @param {object} [params] `{include_past}` to see former members too. */
export function listTeamMembers(id, params = {}) {
  return withMockFallback(
    `GET /teams/${id}/members`,
    async () => (await apiClient.get(`/teams/${id}/members`, { params })).data,
    () => ({ data: [], page: { limit: 50, next_cursor: null, prev_cursor: null, has_more: false } }),
  );
}

/**
 * @param {string} id @param {string[]} userIds
 * @returns {Promise<{added:number, skipped_already_member:number, skipped_invalid:number}>}
 */
export function addTeamMembers(id, userIds) {
  return withMockFallback(
    `POST /teams/${id}/members`,
    async () => (await apiClient.post(`/teams/${id}/members`, { user_ids: userIds })).data,
    () => ({ added: userIds.length, skipped_already_member: 0, skipped_invalid: 0 }),
  );
}

/** Sets `left_on` rather than deleting the row. @param {string} id @param {string} userId */
export function removeTeamMember(id, userId) {
  return withMockFallback(
    `DELETE /teams/${id}/members/${userId}`,
    async () => (await apiClient.delete(`/teams/${id}/members/${userId}`)).data,
    () => ({ deleted: true }),
  );
}

/** Who-is-doing-what for one team: per-member figures, next/overdue activities, recent work, top deals. */
export async function getTeamSummary(id, params = {}) {
  return (await apiClient.get(`/teams/${id}/summary`, { params })).data;
}
